import { motion, AnimatePresence } from "framer-motion";

const FLOAT = { y: [-4, 4, -4] };
const FLOAT_T = { y: { repeat: Infinity, duration: 4, ease: "easeInOut" } };
const CLOSED_INITIAL = { scale: 0, rotate: -15, opacity: 0 };
const CLOSED_ANIMATE = { scale: 1, rotate: -6, opacity: 1, ...FLOAT };
const CLOSED_EXIT = { scale: 0, rotate: 10, opacity: 0 };
const CLOSED_T = { ...FLOAT_T, scale: { duration: 0.35 }, rotate: { duration: 0.35 }, opacity: { duration: 0.25 } };
const CLOSED_HOVER = { scale: 1.06, rotate: 0 };
const MARKER_ANIMATE = { y: [0, -6, 0], rotate: [12, 16, 12] };
const MARKER_T = { repeat: Infinity, duration: 3.5, ease: "easeInOut" };
const OPEN_INITIAL = { scale: 0, rotate: 90 };
const OPEN_ANIMATE = { scale: 1, rotate: 0 };
const OPEN_EXIT = { scale: 0, rotate: -90 };
const OPEN_T = { duration: 0.3 };

function ClipboardSVG() {
  return (
    <svg viewBox="0 0 100 120" className="w-full h-full" style={{ filter: "drop-shadow(0 18px 22px rgba(0,0,0,0.75)) drop-shadow(0 0 18px rgba(56,210,255,0.22))" }} aria-hidden="true">
      <defs>
        <linearGradient id="bl-board" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#0B2A5C" />
          <stop offset="1" stopColor="#02060F" />
        </linearGradient>
        <linearGradient id="bl-chrome" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F3F6FA" />
          <stop offset="0.5" stopColor="#9AA8BC" />
          <stop offset="1" stopColor="#E4EAF3" />
        </linearGradient>
        <linearGradient id="bl-line" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#38D2FF" stopOpacity="0.9" />
          <stop offset="1" stopColor="#4C9DFF" stopOpacity="0.15" />
        </linearGradient>
      </defs>
      <rect x="8" y="12" width="84" height="104" rx="6" fill="url(#bl-board)" stroke="#38D2FF" strokeOpacity="0.55" strokeWidth="1.2" />
      <rect x="16" y="24" width="68" height="84" rx="2" fill="rgba(255,255,255,0.035)" stroke="rgba(228,234,243,0.18)" strokeWidth="0.8" />
      <path d="M24 42h34M24 52h48M24 62h40M24 72h50M24 82h30" stroke="url(#bl-line)" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="70" cy="90" r="4" fill="#38D2FF" fillOpacity="0.9">
        <animate attributeName="fill-opacity" values="0.9;0.3;0.9" dur="2.2s" repeatCount="indefinite" />
      </circle>
      <rect x="34" y="4" width="32" height="16" rx="3" fill="url(#bl-chrome)" />
      <rect x="42" y="8" width="16" height="6" rx="2" fill="#071A3A" />
      <path d="M40 4 v-1 a10 10 0 0 1 20 0 v1" fill="none" stroke="#E4EAF3" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function MarkerSVG() {
  return (
    <svg viewBox="0 0 20 100" className="w-full h-full" style={{ filter: "drop-shadow(-4px 8px 8px rgba(0,0,0,0.7))" }} aria-hidden="true">
      <rect x="4" y="18" width="12" height="70" rx="3" fill="#F3F6FA" />
      <rect x="4" y="18" width="12" height="70" rx="3" fill="url(#bl-line)" opacity="0.15" />
      <rect x="4" y="0" width="12" height="22" rx="3" fill="#071A3A" stroke="#38D2FF" strokeOpacity="0.6" strokeWidth="0.8" />
      <path d="M7 88 L10 100 L13 88 Z" fill="#38D2FF" />
      <rect x="6" y="40" width="8" height="22" rx="1" fill="#0B2A5C" opacity="0.85" />
    </svg>
  );
}

export function LabLauncher({ open, onToggle }) {
  return (
    <div className="fixed bottom-6 right-6 z-[45] group flex flex-col items-center" style={{ perspective: 1000 }}>
      <div className="pointer-events-none absolute -top-10 whitespace-nowrap font-mono text-[10px] tracking-[0.25em] uppercase bg-[#061125] border border-[#38D2FF]/30 text-[#38D2FF] px-3 py-1.5 rounded-full opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300"
        style={{ boxShadow: "0 0 15px rgba(56,210,255,0.2)" }} data-testid="support-chat-label">
        {open ? "Close" : "Ask the Lab"}
      </div>
      <button
        onClick={onToggle}
        data-testid="support-chat-launcher"
        aria-label={open ? "Close support chat" : "Open support chat"}
        aria-expanded={open}
        className="relative min-w-[44px] min-h-[44px] cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[#38D2FF]/60 rounded-xl"
      >
        <AnimatePresence mode="wait" initial={false}>
          {!open ? (
            <motion.div
              key="closed"
              initial={CLOSED_INITIAL}
              animate={CLOSED_ANIMATE}
              exit={CLOSED_EXIT}
              transition={CLOSED_T}
              whileHover={CLOSED_HOVER}
              className="relative w-16 h-20 md:w-20 md:h-24"
            >
              <ClipboardSVG />
              <motion.div
                className="absolute -right-2 top-7 w-4 h-20 md:h-24"
                animate={MARKER_ANIMATE}
                transition={MARKER_T}
              >
                <MarkerSVG />
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={OPEN_INITIAL}
              animate={OPEN_ANIMATE}
              exit={OPEN_EXIT}
              transition={OPEN_T}
              className="w-14 h-14 rounded-full bg-[#071A3A] border border-[#38D2FF]/40 flex items-center justify-center"
              style={{ boxShadow: "0 0 20px rgba(56,210,255,0.3)" }}
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="#38D2FF" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </motion.div>
          )}
        </AnimatePresence>
      </button>
    </div>
  );
}
